import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Typography } from 'antd';
import { 
  UserOutlined, 
  DatabaseOutlined, 
  DollarOutlined,
  CloudServerOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;

interface DashboardStats {
  users: {
    total_users: number;
    active_users: number;
    new_users_today: number;
    total_balance: number;
  };
  servers: {
    total_servers: number;
    running_servers: number;
    stopped_servers: number;
    expired_servers: number;
  };
  revenue: {
    today_revenue: number;
    week_revenue: number;
    month_revenue: number;
    total_revenue: number;
  };
  nodes: {
    total_nodes: number;
    online_nodes: number;
    local_nodes: number;
  };
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);

  useEffect(() => {
    loadDashboardStats();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1200);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDashboardStats = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('加载统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!stats) {
    return <div>加载中...</div>;
  }

  // 确保所有统计数据都有默认值
  const safeStats = {
    users: {
      total_users: stats.users?.total_users || 0,
      active_users: stats.users?.active_users || 0,
      new_users_today: stats.users?.new_users_today || 0,
      total_balance: stats.users?.total_balance || 0,
    },
    servers: {
      total_servers: stats.servers?.total_servers || 0,
      running_servers: stats.servers?.running_servers || 0,
      stopped_servers: stats.servers?.stopped_servers || 0,
      expired_servers: stats.servers?.expired_servers || 0,
    },
    revenue: {
      today_revenue: stats.revenue?.today_revenue || 0,
      week_revenue: stats.revenue?.week_revenue || 0,
      month_revenue: stats.revenue?.month_revenue || 0,
      total_revenue: stats.revenue?.total_revenue || 0,
    },
    nodes: {
      total_nodes: stats.nodes?.total_nodes || 0,
      online_nodes: stats.nodes?.online_nodes || 0,
      local_nodes: stats.nodes?.local_nodes || 0,
    },
  };

  return (
    <div className="fade-in">
      {/* 欢迎区域 */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '32px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '120px',
          height: '120px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <Title level={1} style={{ 
            color: 'white', 
            margin: 0, 
            fontSize: '32px',
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            欢迎回来，管理员！
          </Title>
          <div style={{
            fontSize: '16px',
            marginTop: '8px',
            opacity: 0.9,
            fontWeight: '500'
          }}>
            今天是 {new Date().toLocaleDateString('zh-CN', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              weekday: 'long'
            })}，祝您工作愉快！
          </div>
        </div>
      </div>

      {/* 管理概览标题 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '24px',
        padding: '16px 0'
      }}>
        <div style={{
          width: '4px',
          height: '32px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '2px',
          marginRight: '16px'
        }} />
        <Title level={2} style={{ 
          margin: 0,
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--text-color)'
        }}>
          管理概览
        </Title>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Card className="hover-card">
            <Statistic
              title="总服务器数"
              value={Number(safeStats.servers.total_servers) || 0}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div className="text-sm text-gray-500 mt-2">
              运行中: {safeStats.servers.running_servers}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Card className="hover-card">
            <Statistic
              title="运行中"
              value={Number(safeStats.servers.running_servers) || 0}
              prefix={<CloudServerOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="text-sm text-gray-500 mt-2">
              已停止: {safeStats.servers.stopped_servers}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Card className="hover-card">
            <Statistic
              title="账户余额"
              value={Number(safeStats.users.total_balance) || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#faad14' }}
              suffix="元"
            />
            <div className="text-sm text-gray-500 mt-2">
              总用户: {safeStats.users.total_users}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Card className="hover-card">
            <Statistic
              title="本月消费"
              value={Number(safeStats.revenue.month_revenue) || 0}
              prefix={<UserOutlined />}
              precision={2}
              valueStyle={{ color: '#722ed1' }}
              suffix="元"
            />
            <div className="text-sm text-gray-500 mt-2">
              今日: ¥{(Number(safeStats.revenue.today_revenue) || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '32px' }}>
        <Col xs={24} lg={12}>
          <Card title="用户统计" className="h-64">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span>总用户数</span>
                <span className="font-bold">{safeStats.users.total_users}</span>
              </div>
              <div className="flex justify-between">
                <span>活跃用户</span>
                <span className="font-bold text-green-600">{safeStats.users.active_users}</span>
              </div>
              <div className="flex justify-between">
                <span>今日新增</span>
                <span className="font-bold text-blue-600">{safeStats.users.new_users_today}</span>
              </div>
              <div className="flex justify-between">
                <span>用户总余额</span>
                <span className="font-bold text-orange-600">¥{(Number(safeStats.users.total_balance) || 0).toFixed(2)}</span>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="服务器统计" className="h-64">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span>总服务器数</span>
                <span className="font-bold">{safeStats.servers.total_servers}</span>
              </div>
              <div className="flex justify-between">
                <span>运行中</span>
                <span className="font-bold text-green-600">{safeStats.servers.running_servers}</span>
              </div>
              <div className="flex justify-between">
                <span>已停止</span>
                <span className="font-bold text-red-600">{safeStats.servers.stopped_servers}</span>
              </div>
              <div className="flex justify-between">
                <span>已过期</span>
                <span className="font-bold text-orange-600">{safeStats.servers.expired_servers}</span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-6">
        <Col span={24}>
          <Card title="收入统计">
            <Row gutter={16}>
              <Col xs={24} sm={6}>
                <Statistic
                  title="今日收入"
                  value={Number(safeStats.revenue.today_revenue) || 0}
                  precision={2}
                  suffix="元"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="本周收入"
                  value={Number(safeStats.revenue.week_revenue) || 0}
                  precision={2}
                  suffix="元"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="本月收入"
                  value={Number(safeStats.revenue.month_revenue) || 0}
                  precision={2}
                  suffix="元"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="总收入"
                  value={Number(safeStats.revenue.total_revenue) || 0}
                  precision={2}
                  suffix="元"
                  valueStyle={{ color: '#eb2f96' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;



